import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldOff, ShieldCheck } from 'lucide-react';
import { Button, Card, Badge, toast } from '@employer-on-record/ui';
import { adminApi } from '@/services/api';
import { PageHead } from '@/components/PageHead';
import { Avatar } from '@/components/Avatar';
import { formatDateShort, formatNumber } from '@/lib/utils';

export default function HrUsersPage() {
	const qc = useQueryClient();
	const { data } = useQuery({ queryKey: ['admin-hr-users'], queryFn: () => adminApi.hrUsers() });
	const users = data?.data ?? [];

	const block = useMutation({
		mutationFn: ({ id, isBlocked }: { id: string; isBlocked: boolean }) => adminApi.blockUser(id, isBlocked),
		onSuccess: () => { toast('User updated'); qc.invalidateQueries({ queryKey: ['admin-hr-users'] }); },
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	return (
		<>
			<PageHead title="HR users" subtitle={`${formatNumber(users.length)} HR account${users.length === 1 ? '' : 's'} across all companies`} />
			<Card>
				<table className="w-full text-[13px]">
					<thead>
						<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
							<th className="text-left px-4 py-2.5 font-semibold">User</th>
							<th className="text-left px-4 py-2.5 font-semibold">Company</th>
							<th className="text-left px-4 py-2.5 font-semibold">Status</th>
							<th className="text-left px-4 py-2.5 font-semibold">Created</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{users.length === 0 && (
							<tr><td colSpan={5} className="text-center text-muted-foreground p-10">No HR users yet — open a company and add one.</td></tr>
						)}
						{users.map((u) => (
							<tr key={u.id} className="border-b last:border-0 hover:bg-background/60">
								<td className="px-4 py-3.5">
									<div className="flex items-center gap-2.5">
										<Avatar name={u.name} />
										<div>
											<div className="font-medium">{u.name}</div>
											<div className="text-[11px] text-muted-foreground">{u.email}</div>
										</div>
									</div>
								</td>
								<td className="px-4 py-3.5 text-muted-foreground">{u.company?.name || '—'}</td>
								<td className="px-4 py-3.5"><Badge tone={u.isBlocked ? 'red' : 'green'}>{u.isBlocked ? 'Blocked' : 'Active'}</Badge></td>
								<td className="px-4 py-3.5 text-muted-foreground">{formatDateShort(u.createdAt)}</td>
								<td className="px-4 py-3.5 text-right">
									<Button variant="secondary" size="sm" onClick={() => block.mutate({ id: u.id, isBlocked: !u.isBlocked })}>
										{u.isBlocked ? <><ShieldCheck className="h-3 w-3" /> Unblock</> : <><ShieldOff className="h-3 w-3" /> Block</>}
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</Card>
		</>
	);
}
